import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Search,
  LogOut,
  Languages,
  Lightbulb,
  HeadphonesIcon,
  ArrowUp,
  RotateCw,
  MousePointerClick,
  FileText,
  ListChecks,
  MessageSquare,
  History,
  ArrowLeft,
  Link2,
  Printer,
  Maximize2,
  Scale,
  Eraser,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import {
  PlatformSupportWidget,
  openSupportWidget,
} from '../PlatformSupportWidget';
import { ZoekVenster, type UitvoerModus } from './ZoekVenster';
import type { ZoekItem, ZoekNavItem } from './zoekTypes';
import {
  isRisicovol,
  leesOnthouden,
  markeer,
  onthoudPagina,
  scanPagina,
  vindKnop,
  voerUit,
  wachtOpKnop,
  wisOnthouden,
} from './paginaKnoppen';
import { takenVoorRol } from './zoekTaken';
import { normaliseer } from './zoekMatch';

interface PlatformZoekenProps {
  navigation: ZoekNavItem[];
  paginaTitel?: string;
  /** Feedback- en klantenservice-knoppen tonen (professional); anders alleen het zoekicoon. */
  metSupport: boolean;
  onUitloggen?: () => void;
}

type RecentRegel = Pick<
  ZoekItem,
  'id' | 'titel' | 'pad' | 'href' | 'knopTekst' | 'alleenAanwijzen' | 'stappen'
>;

const MAX_RECENT = 6;

const leesRecent = (sleutel: string): RecentRegel[] => {
  try {
    const data = JSON.parse(localStorage.getItem(sleutel) ?? '[]');
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

/**
 * Zwevend zoekicoon rechtsonder (naast klantenservice, onder Feedback) en het
 * zoek-alles-venster. Doorzoekbaar: het hoofdmenu, vaste taken per rol,
 * knoppen op het huidige scherm en knoppen op eerder bezochte pagina's.
 */
export const PlatformZoeken: React.FC<PlatformZoekenProps> = ({
  navigation,
  paginaTitel,
  metSupport,
  onUitloggen,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore(s => s.user);
  const isImpersonating = useAuthStore(s => s.isImpersonating);
  const [open, setOpen] = useState(false);
  const [schermKnoppen, setSchermKnoppen] = useState<
    ReturnType<typeof scanPagina>
  >([]);
  const [recentVersie, setRecentVersie] = useState(0);

  const gebruiker = user?.id ? String(user.id) : 'gast';
  const recentSleutel = `belagom.zoeken.recent.${gebruiker}`;
  const huidigHref = location.pathname + location.search;

  const titelVanScherm = useCallback((): string => {
    if (paginaTitel) return paginaTitel;
    for (const item of navigation) {
      const sub =
        item.submenu?.find(s => s.href === huidigHref) ??
        item.submenu?.find(s => s.href === location.pathname);
      if (sub) return sub.name;
      if (item.href === huidigHref || item.href === location.pathname)
        return item.name;
    }
    const kop = document.querySelector<HTMLElement>('main h1, main h2');
    return kop?.innerText.trim() || document.title;
  }, [paginaTitel, navigation, huidigHref, location.pathname]);

  // Ctrl/⌘ K opent en sluit het venster overal
  useEffect(() => {
    const onToets = (e: KeyboardEvent) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        !e.altKey &&
        e.key.toLowerCase() === 'k'
      ) {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onToets);
    return () => window.removeEventListener('keydown', onToets);
  }, []);

  // Knoppen van elke bezochte pagina onthouden, zodat ze later ook van elders vindbaar zijn.
  useEffect(() => {
    if (!user) return;
    const timers = [1500, 5000].map(ms =>
      window.setTimeout(
        () =>
          onthoudPagina(gebruiker, huidigHref, titelVanScherm(), scanPagina()),
        ms
      )
    );
    return () => timers.forEach(clearTimeout);
  }, [user, gebruiker, huidigHref, titelVanScherm]);

  useEffect(() => {
    if (!open) return;
    const knoppen = scanPagina();
    setSchermKnoppen(knoppen);
    if (user) onthoudPagina(gebruiker, huidigHref, titelVanScherm(), knoppen);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = useMemo<ZoekItem[]>(() => {
    if (!open) return [];
    const lijst: ZoekItem[] = [];
    const menuLabel = t('zoeken.menu', { defaultValue: 'Menu' });

    // Taken met stappen (per rol)
    for (const taak of takenVoorRol(user?.role, user?.opleider_rol)) {
      lijst.push({ ...taak, groep: 'taken', icoon: ListChecks });
    }

    // Pagina's uit het hoofdmenu
    const gezienHref = new Set<string>();
    for (const item of navigation) {
      if (!gezienHref.has(item.href)) {
        gezienHref.add(item.href);
        lijst.push({
          id: `pagina:${item.href}`,
          groep: 'paginas',
          titel: item.name,
          pad: [menuLabel],
          href: item.href,
          icoon: item.icon ?? FileText,
          stappen: [
            t('zoeken.stap.openMenu', {
              defaultValue: 'Open {{naam}} in het hoofdmenu',
              naam: item.name,
            }),
          ],
        });
      }
      for (const sub of item.submenu ?? []) {
        if (sub.disabled || gezienHref.has(sub.href)) continue;
        gezienHref.add(sub.href);
        lijst.push({
          id: `pagina:${sub.href}`,
          groep: 'paginas',
          titel: sub.name,
          pad: [item.name],
          omschrijving: sub.description,
          href: sub.href,
          icoon: item.icon ?? FileText,
          stappen: [
            t('zoeken.stap.openMenu', {
              defaultValue: 'Open {{naam}} in het hoofdmenu',
              naam: item.name,
            }),
            t('zoeken.stap.kies', {
              defaultValue: 'Kies {{naam}}',
              naam: sub.name,
            }),
          ],
        });
      }
    }
    if (user && !gezienHref.has('/profiel')) {
      const naam = t('zoeken.mijnProfiel', { defaultValue: 'Mijn profiel' });
      lijst.push({
        id: 'pagina:/profiel',
        groep: 'paginas',
        titel: naam,
        pad: [menuLabel],
        href: '/profiel',
        icoon: FileText,
        trefwoorden: ['account', 'wachtwoord', 'gegevens'],
      });
    }

    // Knoppen op dit scherm
    const titel = titelVanScherm();
    schermKnoppen.forEach((k, i) => {
      lijst.push({
        id: `scherm:${i}:${normaliseer(k.label)}`,
        groep: 'opDezePagina',
        titel: k.label,
        omschrijving: k.omschrijving,
        pad: k.context ? [titel, k.context] : [titel],
        element: k.element,
        icoon: MousePointerClick,
        stappen: [
          k.context
            ? t('zoeken.stap.staatOnder', {
                defaultValue: 'Staat op dit scherm onder {{kop}}',
                kop: k.context,
              })
            : t('zoeken.stap.staatHier', {
                defaultValue: 'Staat op dit scherm',
              }),
          t('zoeken.stap.klik', {
            defaultValue: 'Klik {{naam}}',
            naam: k.label,
          }),
        ],
      });
    });

    // Knoppen op eerder bezochte pagina's (niet die van dit scherm, die staan hierboven al)
    const takenKnoppen = new Set(
      lijst
        .filter(i => i.knopTekst)
        .map(i => `${i.href}|${normaliseer(i.knopTekst!)}`)
    );
    const gezienElders = new Set<string>();
    for (const k of user ? leesOnthouden(gebruiker) : []) {
      const sleutel = `${k.href}|${normaliseer(k.label)}`;
      if (
        k.href === huidigHref ||
        takenKnoppen.has(sleutel) ||
        gezienElders.has(sleutel)
      )
        continue;
      gezienElders.add(sleutel);
      lijst.push({
        id: `elders:${sleutel}`,
        groep: 'elders',
        titel: k.label,
        pad: k.context ? [k.paginaTitel, k.context] : [k.paginaTitel],
        href: k.href,
        knopTekst: k.label,
        icoon: MousePointerClick,
        stappen: [
          t('zoeken.stap.open', {
            defaultValue: 'Open {{naam}}',
            naam: k.paginaTitel,
          }),
          t('zoeken.stap.klik', {
            defaultValue: 'Klik {{naam}}',
            naam: k.label,
          }),
        ],
      });
    }

    // Acties
    const acties = t('zoeken.groep.acties', { defaultValue: 'Acties' });
    if (metSupport) {
      lijst.push({
        id: 'actie:feedback',
        groep: 'acties',
        titel: t('zoeken.actie.feedback', { defaultValue: 'Feedback geven' }),
        pad: [acties],
        omschrijving: t('zoeken.actie.feedbackUitleg', {
          defaultValue: 'Deel een verbeterpunt voor het platform',
        }),
        trefwoorden: [
          'verbeterpunt',
          'idee',
          'suggestie',
          'bug',
          'fout melden',
        ],
        icoon: Lightbulb,
        actie: () => openSupportWidget('feedback'),
      });
      lijst.push({
        id: 'actie:klantenservice',
        groep: 'acties',
        titel: t('zoeken.actie.klantenservice', {
          defaultValue: 'Chat met klantenservice',
        }),
        pad: [acties],
        trefwoorden: [
          'hulp',
          'help',
          'support',
          'vraag',
          'contact',
          'helpdesk',
        ],
        icoon: HeadphonesIcon,
        actie: () => openSupportWidget('support'),
      });
    }
    const naarEngels = !i18n.language?.startsWith('en');
    lijst.push({
      id: 'actie:taal',
      groep: 'acties',
      titel: naarEngels ? 'Switch to English' : 'Wissel naar Nederlands',
      pad: [acties],
      trefwoorden: [
        'taal',
        'language',
        'engels',
        'english',
        'nederlands',
        'dutch',
      ],
      icoon: Languages,
      actie: () => i18n.changeLanguage(naarEngels ? 'en' : 'nl'),
    });
    lijst.push({
      id: 'actie:boven',
      groep: 'acties',
      titel: t('zoeken.actie.naarBoven', { defaultValue: 'Naar boven' }),
      pad: [acties],
      trefwoorden: ['begin', 'top', 'scroll'],
      icoon: ArrowUp,
      actie: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
    });
    lijst.push({
      id: 'actie:vernieuwen',
      groep: 'acties',
      titel: t('zoeken.actie.vernieuwen', {
        defaultValue: 'Pagina vernieuwen',
      }),
      pad: [acties],
      trefwoorden: ['herladen', 'reload', 'refresh'],
      icoon: RotateCw,
      actie: () => window.location.reload(),
    });
    if (window.history.length > 1) {
      lijst.push({
        id: 'actie:terug',
        groep: 'acties',
        titel: t('zoeken.actie.terug', {
          defaultValue: 'Terug naar vorige pagina',
        }),
        pad: [acties],
        trefwoorden: ['vorige', 'back', 'terug'],
        icoon: ArrowLeft,
        actie: () => navigate(-1),
      });
    }
    lijst.push({
      id: 'actie:kopieer',
      groep: 'acties',
      titel: t('zoeken.actie.kopieerLink', {
        defaultValue: 'Link naar deze pagina kopiëren',
      }),
      pad: [acties],
      trefwoorden: ['url', 'delen', 'kopieer', 'link', 'share'],
      icoon: Link2,
      actie: () =>
        navigator.clipboard
          ?.writeText(window.location.href)
          .then(() =>
            toast.success(
              t('zoeken.actie.linkGekopieerd', {
                defaultValue: 'Link gekopieerd',
              })
            )
          )
          .catch(() =>
            toast.error(
              t('zoeken.actie.kopierenMislukt', {
                defaultValue: 'Kopiëren lukte niet',
              })
            )
          ),
    });
    lijst.push({
      id: 'actie:afdrukken',
      groep: 'acties',
      titel: t('zoeken.actie.afdrukken', { defaultValue: 'Pagina afdrukken' }),
      pad: [acties],
      trefwoorden: ['printen', 'print', 'pdf', 'afdruk'],
      icoon: Printer,
      // Eerst het zoekvenster laten verdwijnen, anders staat het op de afdruk
      actie: () => window.setTimeout(() => window.print(), 300),
    });
    lijst.push({
      id: 'actie:volledigScherm',
      groep: 'acties',
      titel: document.fullscreenElement
        ? t('zoeken.actie.volledigSchermUit', {
            defaultValue: 'Volledig scherm verlaten',
          })
        : t('zoeken.actie.volledigScherm', { defaultValue: 'Volledig scherm' }),
      pad: [acties],
      trefwoorden: ['fullscreen', 'groot', 'scherm', 'presenteren'],
      icoon: Maximize2,
      actie: () =>
        (document.fullscreenElement
          ? document.exitFullscreen()
          : document.documentElement.requestFullscreen()
        ).catch(() => undefined),
    });
    if (user) {
      lijst.push({
        id: 'actie:klachtenreglement',
        groep: 'acties',
        titel: t('zoeken.actie.klachtenreglement', {
          defaultValue: 'Klachtenreglement lezen',
        }),
        pad: [acties],
        omschrijving: t('zoeken.actie.klachtenreglementUitleg', {
          defaultValue:
            'B2B-klachtenreglement en escalatielijn, met PDF-download',
        }),
        trefwoorden: [
          'klacht',
          'reglement',
          'escalatie',
          'geschil',
          'voorwaarden',
        ],
        icoon: Scale,
        actie: () => {
          const knop = vindKnop(
            t('footer.link', {
              ns: 'klachten',
              defaultValue: 'B2B Klachtenreglement & Escalatielijn',
            })
          );
          if (knop) knop.click();
          else
            toast.error(
              t('zoeken.knopNietGevonden', {
                defaultValue:
                  'Je bent op de juiste pagina; de knop "{{naam}}" is nu niet zichtbaar.',
                naam: 'Klachtenreglement',
              })
            );
        },
      });
      lijst.push({
        id: 'actie:geschiedenisWissen',
        groep: 'acties',
        titel: t('zoeken.actie.geschiedenisWissen', {
          defaultValue: 'Zoekgeschiedenis wissen',
        }),
        pad: [acties],
        omschrijving: t('zoeken.actie.geschiedenisWissenUitleg', {
          defaultValue:
            'Recent gebruikte items en onthouden knoppen in deze browser vergeten',
        }),
        trefwoorden: [
          'recent',
          'geschiedenis',
          'wissen',
          'vergeten',
          'privacy',
        ],
        icoon: Eraser,
        actie: () => {
          wisOnthouden(gebruiker);
          try {
            localStorage.removeItem(recentSleutel);
          } catch {
            // Geen opslag: niets te wissen.
          }
          toast.success(
            t('zoeken.actie.geschiedenisGewist', {
              defaultValue: 'Zoekgeschiedenis gewist',
            })
          );
        },
      });
    }
    if (user && onUitloggen) {
      lijst.push({
        id: 'actie:uitloggen',
        groep: 'acties',
        // Tijdens impersonatie brengt uitloggen de admin terug naar het eigen account
        titel: isImpersonating
          ? t('auth.returnToAdmin', { defaultValue: 'Terug naar Admin' })
          : t('zoeken.actie.uitloggen', { defaultValue: 'Uitloggen' }),
        pad: [acties],
        trefwoorden: [
          'afmelden',
          'logout',
          'log out',
          'uit',
          'admin',
          'impersonatie',
        ],
        icoon: LogOut,
        actie: onUitloggen,
      });
    }
    return lijst;
  }, [
    open,
    navigation,
    schermKnoppen,
    user,
    gebruiker,
    huidigHref,
    metSupport,
    onUitloggen,
    isImpersonating,
    recentSleutel,
    navigate,
    t,
    i18n,
    titelVanScherm,
  ]);

  const recent = useMemo<ZoekItem[]>(() => {
    if (!open) return [];
    return leesRecent(recentSleutel)
      .map<ZoekItem | undefined>(r => {
        const actueel = items.find(
          i => i.id === r.id && i.groep !== 'opDezePagina'
        );
        if (actueel) return { ...actueel, groep: 'recent' };
        return r.href ? { ...r, groep: 'recent', icoon: History } : undefined;
      })
      .filter((i): i is ZoekItem => !!i);
    // recentVersie: localStorage opnieuw lezen nadat een item is uitgevoerd
  }, [open, items, recentSleutel, recentVersie]); // eslint-disable-line react-hooks/exhaustive-deps

  const bewaarRecent = (item: ZoekItem) => {
    // Een knop op dit scherm onthouden we als "pagina + knop", zodat hij later ook werkt.
    const regel: RecentRegel = item.element
      ? {
          id: `elders:${huidigHref}|${normaliseer(item.titel)}`,
          titel: item.titel,
          pad: item.pad,
          href: huidigHref,
          knopTekst: item.titel,
          stappen: item.stappen,
        }
      : {
          id: item.id,
          titel: item.titel,
          pad: item.pad,
          href: item.href,
          knopTekst: item.knopTekst,
          alleenAanwijzen: item.alleenAanwijzen,
          stappen: item.stappen,
        };
    try {
      const lijst = [
        regel,
        ...leesRecent(recentSleutel).filter(r => r.id !== regel.id),
      ].slice(0, MAX_RECENT);
      localStorage.setItem(recentSleutel, JSON.stringify(lijst));
    } catch {
      // Geen opslag: dan geen recent-lijst.
    }
    setRecentVersie(v => v + 1);
  };

  const bevestigd = (label: string) =>
    !isRisicovol(label) ||
    window.confirm(
      t('zoeken.bevestig', {
        defaultValue: '"{{naam}}" nu uitvoeren?',
        naam: label,
      })
    );

  const uitvoeren = async (item: ZoekItem, modus: UitvoerModus) => {
    setOpen(false);
    bewaarRecent(item);

    if (item.actie) {
      item.actie();
      return;
    }
    if (item.element) {
      if (!item.element.isConnected) {
        toast.error(
          t('zoeken.nietMeerOpScherm', {
            defaultValue: 'Deze knop staat niet meer op het scherm.',
          })
        );
        return;
      }
      if (modus === 'doe' && bevestigd(item.titel)) voerUit(item.element);
      else markeer(item.element);
      return;
    }
    if (!item.href) return;

    if (item.href !== huidigHref) navigate(item.href);
    if (!item.knopTekst) return;

    const el = await wachtOpKnop(item.knopTekst, item.pad?.[1]);
    if (!el) {
      toast(
        t('zoeken.knopNietGevonden', {
          defaultValue:
            'Je bent op de juiste pagina; de knop "{{naam}}" is nu niet zichtbaar.',
          naam: item.knopTekst,
        })
      );
      return;
    }
    if (modus === 'doe' && bevestigd(item.knopTekst)) voerUit(el);
    else markeer(el);
  };

  const vraagItems = useCallback(
    (vraag: string): ZoekItem[] => {
      if (vraag.trim().length < 3) return [];
      if (metSupport) {
        return [
          {
            id: 'vraag:klantenservice',
            groep: 'vraag',
            titel: t('zoeken.vraagKlantenservice', {
              defaultValue: 'Vraag de klantenservice: {{vraag}}',
              vraag: vraag.trim(),
            }),
            icoon: HeadphonesIcon,
            omschrijving: t('zoeken.vraagKlantenserviceUitleg', {
              defaultValue:
                'Niet gevonden wat je zocht? Stel je vraag in de chat met de klantenservice.',
            }),
            actie: () => openSupportWidget('support'),
          },
        ];
      }
      if (user?.role === 'opdrachtgever' || user?.role === 'employer_member') {
        return [
          {
            id: 'vraag:belagom',
            groep: 'vraag',
            titel: t('zoeken.vraagBelagom', {
              defaultValue: 'Stel je vraag aan BeLagom: {{vraag}}',
              vraag: vraag.trim(),
            }),
            icoon: MessageSquare,
            href: '/berichten?tab=belagom',
          },
        ];
      }
      return [];
    },
    [metSupport, t, user?.role]
  );

  const voorbeelden = useMemo(
    () =>
      takenVoorRol(user?.role, user?.opleider_rol)
        .slice(0, 6)
        .map(tk => tk.titel),
    [user?.role, user?.opleider_rol]
  );

  const zoekKnop = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label={t('zoeken.knop', { defaultValue: 'Zoeken (Ctrl K)' })}
      title={t('zoeken.knop', { defaultValue: 'Zoeken (Ctrl K)' })}
      className="w-12 h-12 rounded-full bg-white border border-gray-200 text-[#0B1F3A] shadow-lg flex items-center justify-center hover:shadow-xl transition-shadow"
    >
      <Search size={20} />
    </button>
  );

  return (
    <>
      {metSupport ? (
        <PlatformSupportWidget zoekKnop={zoekKnop} />
      ) : (
        <div
          data-zoek-negeer
          className="fixed bottom-5 right-5 z-50 print:hidden"
        >
          {zoekKnop}
        </div>
      )}
      {open && (
        <ZoekVenster
          items={items}
          recent={recent}
          voorbeelden={voorbeelden}
          vraagItems={vraagItems}
          onUitvoeren={uitvoeren}
          onSluit={() => setOpen(false)}
        />
      )}
    </>
  );
};
